import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import useTapLocationTask from "../../core/useTapLocationTask";
import { hitsZone } from "../../core/zones";
import CandleChart from "../../candle/CandleChart";
import { candleDestinations, candleZones, elementAnchor } from "../../candle/candleModel";
import NoWickDiagram from "./NoWickDiagram";
import Score from "./Score";
import Sentences from "../../core/Sentences";
import { COPY, PROMPTS, READ } from "./config";
import { explainCandleAnatomy } from "./explain";
import useWindowWidth from "../../../../hooks/useWindowWidth";
import { DEFAULT_TOLERANCE_SHARE } from "../../candle/candleModel";

const FOCUS =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1A1A1A]";

// One Candle Anatomy task on one candle: the six prompts, answered by tapping
// an unmarked candle. Guided (Beat 1), or unguided (Call It and every retry).
// After Submit it shows the all-correct reveal itself; when anything is wrong
// the Read swaps in the failure reveal (FailureReveal.jsx) instead.
//
//   mode          "guided" | "unguided"
//   hintEnabled   offer "Need a hint?" after inactivity (off for launch, READ.hint)
//   onHintOpened  the learner opened the hint (forfeits the bonus)
//   showNoWick    include the no-wick teaching visual in the reveal (Beat 3)
//   onSubmitted   (results) graded answer, as soon as Submit is pressed
//   onContinue    (results) the learner pressed the primary button to move on
//   continueLabel (results) => label for that button
export default function CandleTapTask({
  ohlc,
  mode,
  showNoWick,
  hintEnabled,
  onHintOpened,
  onSubmitted,
  onContinue,
  continueLabel,
}) {
  const zones = useMemo(() => candleZones(ohlc), [ohlc]);
  const prompts = useMemo(() => PROMPTS.map((p) => ({ ...p, zone: zones[p.id] })), [zones]);
  const guided = mode === "guided";
  // Teach holds on each step after a correct tap, so the explanation shown is
  // always the one for the step on screen; "Next" moves on.
  const task = useTapLocationTask(prompts, mode, { holdAfterCorrect: guided });
  const { active, activeIndex, answers, results, miss, held, guidedComplete } = task;

  const [announcement, setAnnouncement] = useState("");
  const [lastTeach, setLastTeach] = useState(null);
  const [visibleMiss, setVisibleMiss] = useState(null);
  const [hintAvailable, setHintAvailable] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);

  // The guided soft cue disappears on its own.
  useEffect(() => {
    if (!miss) return undefined;
    setVisibleMiss(miss);
    const t = setTimeout(() => setVisibleMiss(null), 800);
    return () => clearTimeout(t);
  }, [miss]);

  // "Need a hint?" appears after a period with no taps (only when enabled).
  useEffect(() => {
    if (!hintEnabled || results || hintAvailable) return undefined;
    const t = setTimeout(() => setHintAvailable(true), READ.hint.delayMs);
    return () => clearTimeout(t);
  }, [hintEnabled, results, hintAvailable, answers]);

  const openHint = () => {
    setHintOpen(true);
    if (onHintOpened) onHintOpened();
  };

  const handleTap = (pt) => {
    const promptLabel = active && active.label;
    const res = task.tap(pt);
    if (!res) return;
    if (guided) {
      const p = prompts.find((x) => x.id === res.promptId);
      if (res.correct) {
        setLastTeach(p.teach);
        setAnnouncement(`${promptLabel} placed. ${p.teach}`);
      } else {
        setAnnouncement("Try again.");
      }
    } else {
      setAnnouncement(`${promptLabel} recorded.`);
    }
  };

  const handleSubmit = () => {
    const graded = task.submit();
    if (!graded) return;
    setAnnouncement(graded.allCorrect ? "All six correct." : "Answers checked. See the explanation below.");
    if (onSubmitted) onSubmitted(graded);
  };

  const handleNext = () => {
    setLastTeach(null);
    task.next();
    // The next step starts with the whole candle in view.
    if (fit) window.scrollTo({ top: 0 });
  };

  // --- what the chart shows -------------------------------------------------
  const markers = useMemo(() => {
    if (results) {
      const list = [];
      prompts.forEach((p) => {
        const r = results.byId[p.id];
        list.push({
          key: `${p.id}-you`,
          x: r.answer.x,
          y: r.answer.y,
          state: r.correct ? "correct" : "incorrect",
          label: p.label,
          // No "Correct" under the label: the "X of 6 correct" line says it.
          ...(r.correct ? { sub: null } : {}),
          side: "left",
        });
      });
      prompts.forEach((p) => {
        if (results.byId[p.id].correct) return;
        const a = elementAnchor(ohlc, p.id);
        list.push({ key: `${p.id}-fix`, x: a.x, y: a.y, state: "corrected", label: p.label, side: "right" });
      });
      return list;
    }
    return prompts
      .filter((p) => answers[p.id])
      .map((p) => {
        if (guided) {
          // A correct guided tap is shown where the element really is. Its
          // label goes on the right: the left column holds the callout.
          const a = elementAnchor(ohlc, p.id);
          return { key: p.id, x: a.x, y: a.y, state: "teach", label: p.label, side: "right" };
        }
        return { key: p.id, x: answers[p.id].x, y: answers[p.id].y, state: "neutral", label: p.label, side: "left" };
      });
  }, [answers, guided, ohlc, prompts, results]);

  // No gold outline around the body or wicks once an element is placed, nor on
  // the all-correct screen (client: it "adds clutter and competes with the
  // markers"). The dashed teaching zone while a prompt is active stays.

  const showTarget = guided && active && !guidedComplete && !held;
  const zoneHints = showTarget ? active.zone : [];
  const target =
    showTarget
      ? { point: elementAnchor(ohlc, active.id), title: active.title, sub: active.sub }
      : null;

  const destinations = useMemo(() => {
    const list = candleDestinations(ohlc);
    if (!guided || !active) return list;
    // Guided only: the teaching target is announced, as it is shown visually.
    return list.map((d) => (hitsZone(d.point, active.zone) ? { ...d, label: `${d.label}, teaching target` } : d));
  }, [active, guided, ohlc]);

  const explanation = results ? explainCandleAnatomy(ohlc, zones, results) : null;
  const inProgress = !results && !guidedComplete;

  // Phone: a step in progress fits one screen when it can (client: "Title,
  // phase label, candle, answer buttons, explanation, and Next should all be
  // visible without scrolling"). The task is at least the card's visible height
  // (.edge-fit) and the candle gets what is left, measured; only the chart's
  // vertical scale changes, so grading is identical. Small-screen order
  // (client): tighten spacing, then compress type and spacing (edge.css, short
  // screens), but never shrink the candle below MIN_PLOT_PX; past that the card
  // grows and scrolls. Desktop and the reveals flow as before.
  const fit = useWindowWidth() < 768 && (inProgress || (guided && guidedComplete));
  const chartBox = useRef(null);
  const { height: chartHeight, minPx: chartMinPx } = useFitHeight(chartBox, fit);

  // When the card scrolls (the candle is at its floor), a correct Teach tap
  // could leave the explanation and Next below the screen, and the sixth Call
  // It answer could leave Submit there; bring them into view (client: "Do not
  // make the learner scroll to find it").
  const actionSlot = useRef(null);
  const actionReady = guided ? held || guidedComplete : task.canSubmit && !results;
  useEffect(() => {
    if (!fit || !actionReady || !actionSlot.current) return;
    const below = actionSlot.current.getBoundingClientRect().bottom - window.innerHeight;
    if (below <= 0) return;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollBy({ top: below + 12, behavior: reduce ? "auto" : "smooth" });
  }, [fit, actionReady]);

  return (
    <div className={fit ? "edge-fit flex flex-col" : ""}>
      <h1 className="edge-title font-montserrat font-bold text-title-subpage text-pageTitle">Candle Anatomy</h1>
      {/* Client: make the phase visible in the progress label —
          "TEACH · 1 OF 6", then "CALL IT · 1 OF 6" in the unguided section. */}
      {((inProgress && active) || (guided && guidedComplete)) && (
        <p className="mt-0.5 text-xs font-semibold uppercase tracking-wider text-secondary">
          {guided ? "Teach" : "Call it"} · {guidedComplete ? prompts.length : activeIndex + 1} of {prompts.length}
        </p>
      )}

      {/* Guided: the instruction is drawn as a callout beside the candle, so
          it is repeated here for screen readers only. */}
      {inProgress && active && guided && (
        <p className="sr-only">
          {active.title}. {active.sub}
        </p>
      )}
      {inProgress && active && !guided && (
        <div className="edge-gap edge-box mt-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
          <p className="font-semibold text-base text-bodyInk">{active.title}</p>
        </div>
      )}

      {/* Every reveal opens with the same "X of 6 correct" line (client: no
          screen keeps "Correct" in the labels without this header). */}
      {results && (
        <Score correct={prompts.filter((p) => results.byId[p.id].correct)} total={prompts.length} />
      )}

      {/* On a phone the chart is drawn over its box (absolute), so the box's
          size comes only from the layout: it grows into the space left, down to
          the floor, and the chart is then sized to it. */}
      <div
        ref={chartBox}
        className={`mx-auto mt-1 md:mt-3 w-full max-w-[440px] ${fit ? "relative grow basis-0" : ""}`}
        style={fit && chartMinPx ? { minHeight: chartMinPx } : undefined}
      >
        <div className={fit ? "absolute inset-0 flex flex-col justify-center" : ""}>
          <CandleChart
            ohlc={ohlc}
            heights={fit && chartHeight ? { mobile: chartHeight } : undefined}
            onTap={handleTap}
            disabled={!inProgress || !!held}
            zoneHints={zoneHints}
            target={target}
            markers={markers}
            miss={visibleMiss}
            destinations={inProgress ? destinations : []}
            destinationsLabel={active ? `Choose where ${active.label} is` : undefined}
          />
        </div>
      </div>

      {/* Guided keeps the buttons up after the last step too, so the candle
          does not jump when the Wick explanation appears. */}
      {/* Six in a row; on a screen under 360px wide (a small phone, or a larger
          one with the browser's text size turned up) the labels no longer fit
          six across, so they go to two rows of three. */}
      {(inProgress || (guided && guidedComplete)) && (
        <div className="edge-gap mt-2 md:mt-3 grid grid-cols-3 min-[360px]:grid-cols-6 gap-1.5 md:gap-2" role="list" aria-label="Candle elements">
          {prompts.map((p, i) => {
            const isActive = active && active.id === p.id;
            const answered = !!answers[p.id];
            if (guided) {
              // As on the reference board: while the price points are being
              // placed, Body and Wick (a later group) are greyed; the other
              // price points stay at full strength.
              const isRegion = (id) => id === "body" || id === "wick";
              const future = i > activeIndex && isRegion(p.id) !== isRegion(active ? active.id : p.id);
              return (
                <div
                  key={p.id}
                  role="listitem"
                  aria-current={isActive ? "step" : undefined}
                  className={`min-h-[44px] flex items-center justify-center rounded-lg text-[13px] font-medium border ${
                    isActive
                      ? "border-2 border-primaryColor text-primaryColor"
                      : future
                      ? "border-gray-100 bg-gray-50 text-tertiary"
                      : "border-gray-200 text-bodyInk"
                  }`}
                >
                  {p.label}
                </div>
              );
            }
            return (
              <div key={p.id} role="listitem">
                <button
                  type="button"
                  onClick={() => task.selectPrompt(p.id)}
                  aria-pressed={isActive}
                  aria-label={`${p.label}${answered ? ", recorded" : ", not placed yet"}`}
                  className={`relative w-full min-h-[44px] rounded-lg text-[13px] font-medium bg-white ${FOCUS} ${
                    isActive ? "border-2 border-primaryColor text-primaryColor" : "border border-gray-200 text-bodyInk"
                  }`}
                >
                  {p.label}
                  {answered && (
                    <span aria-hidden="true" className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-primaryColor" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* The explanation and Next share one slot, sized for the longest Teach
          explanation (an invisible copy underneath), so the candle does not
          jump when they appear after a correct tap. */}
      {guided && (
        <div ref={actionSlot} className="edge-gap mt-3 md:mt-4 grid">
          <div aria-hidden="true" className="invisible [grid-area:1/1]">
            <TeachExplanation text={LONGEST_TEACH} />
            <PrimaryButton>Next</PrimaryButton>
          </div>
          <div className="[grid-area:1/1] flex flex-col justify-end">
            {(held || guidedComplete) && lastTeach && <TeachExplanation text={lastTeach} />}
            {held && !guidedComplete && <PrimaryButton onClick={handleNext}>Next</PrimaryButton>}
            {guidedComplete && onContinue && <PrimaryButton onClick={() => onContinue(null)}>Continue</PrimaryButton>}
          </div>
        </div>
      )}

      {!guided && !results && (
        <div ref={actionSlot} className="edge-gap mt-3 md:mt-4">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!task.canSubmit}
            className={`w-full min-h-[44px] rounded-lg font-semibold ${FOCUS} ${
              task.canSubmit ? "bg-primaryColor text-white" : "bg-gray-100 text-tertiary cursor-not-allowed"
            }`}
          >
            Submit
          </button>
          {/* Space kept once all six are placed, so the candle does not jump. */}
          <p
            aria-hidden={task.canSubmit}
            className={`mt-2 text-center text-sm text-secondary ${task.canSubmit ? "invisible" : ""}`}
          >
            {COPY.submitGate}
          </p>
          {hintEnabled && hintAvailable && !hintOpen && (
            <div className="mt-2 text-center">
              <button type="button" onClick={openHint} className={`text-sm font-semibold text-linkTeal ${FOCUS}`}>
                Need a hint?
              </button>
            </div>
          )}
          {hintOpen && (
            <div className="mt-3 flex items-start gap-3 rounded-xl border border-[#D7C588] bg-[#D7C588]/10 px-4 py-3">
              <BulbIcon />
              <p className="text-sm text-bodyInk">{COPY.hint}</p>
            </div>
          )}
        </div>
      )}

      {results && (
        <div className="mt-4">
          <div className="rounded-xl border border-gray-200 px-4 py-3">
            <p className="font-semibold text-bodyInk">Explanation</p>
            {explanation.map((line) => (
              <Sentences key={line} text={line} className="mt-1 text-sm text-bodyInk" />
            ))}
          </div>
          {showNoWick && <NoWickDiagram />}
          {onContinue && (
            <button
              type="button"
              onClick={() => onContinue(results)}
              className={`mt-4 w-full min-h-[44px] rounded-lg bg-primaryColor text-white font-semibold ${FOCUS}`}
            >
              {continueLabel ? continueLabel(results) : "Continue"}
            </button>
          )}
        </div>
      )}

      <div aria-live="polite" className="sr-only">
        {announcement}
      </div>
    </div>
  );
}

const LONGEST_TEACH = PROMPTS.reduce((a, p) => (p.teach.length > a.length ? p.teach : a), "");

function TeachExplanation({ text }) {
  return (
    <div className="edge-box flex items-start gap-3 rounded-xl border border-gray-200 px-4 py-3">
      <BulbIcon />
      <Sentences text={text} className="edge-text text-sm md:text-base text-primaryColor" />
    </div>
  );
}

function PrimaryButton({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      tabIndex={onClick ? undefined : -1}
      className={`edge-gap mt-3 md:mt-4 w-full min-h-[44px] rounded-lg bg-primaryColor text-white font-semibold ${FOCUS}`}
    >
      {children}
    </button>
  );
}

// The floor for the candle (client: "Candle legibility and tap accuracy are
// the floor"). A price point's tap band reaches DEFAULT_TOLERANCE_SHARE of the
// plot height above and below it, so a 44px target (the usual minimum for a
// comfortable tap) needs a plot of 22 / share px, about 315px. The plot is the
// chart minus 18 viewBox units top and bottom (CandleChart). Below this the
// card scrolls instead.
const MIN_TARGET_PX = 44;
const MIN_PLOT_PX = Math.ceil(MIN_TARGET_PX / 2 / DEFAULT_TOLERANCE_SHARE);
const PLOT_MARGIN = 36;
const VIEWBOX_W = 360;
const MAX_VIEWBOX_H = 560;

// The chart's viewBox height that fills the box (width 360 in viewBox units),
// re-measured when the box changes size (rotation, browser bars, text size),
// and the box's minimum height in px for the floor at the current width.
function useFitHeight(ref, enabled) {
  const [size, setSize] = useState({ height: null, minPx: null });
  useLayoutEffect(() => {
    if (!enabled || !ref.current) return undefined;
    const el = ref.current;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      if (!width) return;
      const k = width / VIEWBOX_W; // px per viewBox unit
      const minPx = Math.ceil(MIN_PLOT_PX + PLOT_MARGIN * k);
      const h = Math.max(height, minPx) / k;
      setSize({ height: Math.round(Math.min(MAX_VIEWBOX_H, h)), minPx });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, enabled]);
  return enabled ? size : { height: null, minPx: null };
}

function BulbIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" className="shrink-0 mt-0.5">
      <path
        d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.7.5 1.1 1.3 1.1 2.2h5c0-.9.4-1.7 1.1-2.2A6 6 0 0 0 12 3z"
        fill="#D7C588"
        fillOpacity="0.35"
        stroke="#1A1A1A"
        strokeOpacity="0.6"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
